import * as three from "three";

// Accumulator pass shaders: composite particle buffer to screen with trails.
import ACCUMULATOR_FRAGMENT from "../../shaders/particlelayer/accumulator.fs.glsl";
import ACCUMULATOR_VERTEX from "../../shaders/particlelayer/accumulator.vs.glsl";

// Particle draw shaders: render each particle as points or line segments.
import PARTICLE_FRAGMENT from "../../shaders/particlelayer/particle/fs.glsl";
import PARTICLE_LINE_VERTEX from "../../shaders/particlelayer/particle/line.vs.glsl";
import PARTICLE_POINT_VERTEX from "../../shaders/particlelayer/particle/point.vs.glsl";

// RTT (render-to-texture) shaders: GPGPU pass to advance particle state on the GPU.
import RTT_FRAGMENT from "../../shaders/particlelayer/rtt.fs.glsl";
import RTT_VERTEX from "../../shaders/particlelayer/rtt.vs.glsl";

import { TexturePreview, USE_DEBUG_PARTICLES_TEXTURE, USE_DEBUG_PARTICLES_TEXTURE_PREVIEW } from "../../debug";
import type { ColoringFragmentBase } from "../../core/ColoringFragment";
import { GLOBE_MAX_ZOOM } from "../../tiles/constants";
import { EARTH_PERIMETER_M, floatifyColor } from "../../utils";

import { TileLayer, type TileLayerOptions } from "../TileLayer";
import type { ParticleLayerOptions } from "./type";
import { getGridSizeForPitchAndMapDimensions } from "../../tiles/utils";
import { SeededRandomGenerator } from "../../utils/random";

/**
 * High-level overview:
 * `ParticleLayer` extends {@link TileLayer} to visualize directional gridded data as animated particles.
 * Instead of showing only a static field, it presents the flow as motion, so direction and intensity
 * can be understood at a glance.
 *
 * How it works:
 * - A GPGPU-based simulation evolves particle positions directly on the GPU from the underlying tile data.
 * - Renders a texture that contains the particle positions and a normalized scalar derived from the decoded color channels (rtt textures)
 * - Reads from this texture to compute the motion of the particles and the scalar value at the particle's current position.
 * - Creates a buffer geometry that contains the particle positions and the scalar value at the particle's current position.
 * - Ping-pong render targets are used to advance state frame-to-frame without CPU particle updates.
 * - The visual result is temporally accumulated between frames to create smooth trails/fading motion.
 * - The same data can still be shown as raster, so particle motion can complement scalar coloring.
 *
 * Subclasses define how source values are interpreted, while this base class handles the animated
 * flow rendering behavior.
 */
class ParticleLayer extends TileLayer {
  // RTT pass fields to calculate particle positions and scalar values on the GPU.
  private rttScene: three.Scene = new three.Scene();
  private rttCamera!: three.Camera;
  private rttMaterial!: three.RawShaderMaterial;
  private rttMesh!: three.Mesh;

  // Renders the particle positions, color, and size.
  private particleTexturePrev!: three.Texture;
  private particleTexture0!: three.WebGLRenderTarget;
  private particleTexture1!: three.WebGLRenderTarget;
  private particleMaterial!: three.RawShaderMaterial;
  private particles!: three.Points | three.LineSegments;
  private particleBackground!: three.Mesh;

  // Accumulator pass: renders trails.
  private accumulator!: three.WebGLRenderTarget;
  private accumulatorDrawMesh!: three.Mesh;

  private flipFlop = false;

  /** Particle simulation parameters */
  /** Number of particles to be created. */
  private numParticles!: number;
  /** Number of particles visible per 1000 px^2. */
  private particleDensity!: number;
  /** Refresh interval in milliseconds. */
  private refreshInterval!: number;
  /** Fade factor for the trails. */
  private fadeFactor!: number;
  /** Color of the particles. */
  private particleColor!: string;
  /** Color of the particles when moving fast. */
  private particleFastColor!: string;
  /** Speed of the particles when moving fast. */
  private particleFastSpeed!: number;
  /** Size of the particles. */
  private particleSize!: number;
  /** Whether the particles should be drawn as lines. */
  private drawAsLines!: boolean;
  /** Speed of the particles. */
  private particleSpeed!: number;
  /** Pixel ratio for the particles. */
  private pixelRatio!: number;
  /** Whether the trails should be rendered. */
  private renderTrails = true;

  /** Previous render time in milliseconds. */
  private prevRenderTime = 0;
  /** Last render time in milliseconds. */
  private lastRenderTime = 0;
  /** Whether the render should be forced. */
  private forceRender = true;

  /** RTT timestep in milliseconds. */
  private rttTimestep = 10;
  /** Tile grid pass. */
  private tileGridPass = 0;

  /** Upper bound on |decoded field vector| when encoding a normalized scalar (see {@link ParticleLayerOptions.decodeMagnitudeMax}). */
  private decodeMagnitudeMax = 1;

  // private textureScale = 1;
  private particlesTexturePreview: TexturePreview | undefined;

  // Seeded random number generator for particle initialization
  // // (we need this to be more deterministic for testing purposes)
  private randomNumberGen = new SeededRandomGenerator(1);

  /**
   * @param id Unique identifier of the layer in the map.
   * @param options
   * @param particleOptions
   * @param coloringFragments If not null, the same data will also be displayed as raster.
   */
  constructor(
    id: string,
    options: TileLayerOptions | null,
    particleOptions: ParticleLayerOptions | null,
    coloringFragments?: ColoringFragmentBase[] | null,
  ) {
    super(id, null, null);

    if (options && particleOptions) {
      this.initParticle(options, particleOptions, coloringFragments);
    }

    if (USE_DEBUG_PARTICLES_TEXTURE_PREVIEW === true) {
      this.particlesTexturePreview = new TexturePreview();
    }
  }

  /**
   * initParticle
   * - determines params from the options and particle options and creates:
   *  - a random particle texture
   *  - two ping-pong render targets for particle state
   *  - an RTT scene/material that advances particle positions from decoded tile values
   *  - a particle draw material and geometry
   *  - an accumulator render target used to build frame trails/fade
   *  - a particle background mesh
   *  - a particle material
   *  - a particle buffer geometry
   *  - a particle result material
   *  - a particle result geometry
   * @param options - The layer options.
   * @param particleOptions - The particle options.
   * @param coloringFragments - The coloring fragments (not used in this layer).
   */
  public initParticle(
    options: TileLayerOptions,
    particleOptions: ParticleLayerOptions,
    coloringFragments?: ColoringFragmentBase[] | null,
  ) {
    this.init(options, coloringFragments, null);

    this.extentScale = 1.2;

    this.numParticles = 128;

    if (particleOptions.maxAmount != null) {
      if (
        particleOptions.maxAmount >= 4 &&
        particleOptions.maxAmount &&
        (particleOptions.maxAmount & (particleOptions.maxAmount - 1)) === 0
      ) {
        this.numParticles = particleOptions.maxAmount;
      } else {
        throw new Error("The number of particles needs to be power of two and at least 4");
      }
    }

    const dpr = window.devicePixelRatio;

    this.particleDensity = particleOptions.density ?? 2;
    this.particleDensity /= dpr * dpr;

    this.refreshInterval = particleOptions.refreshInterval ?? 800;
    this.fadeFactor = particleOptions.fadeFactor ?? 0.1;
    this.particleColor = floatifyColor(particleOptions.color ?? [255, 255, 255, 192]);
    this.particleFastColor = particleOptions.fastColor ? floatifyColor(particleOptions.fastColor) : this.particleColor;
    this.particleFastSpeed = (particleOptions.fastSpeed ?? 2) * dpr;
    this.particleSize = particleOptions.size ?? 1;

    // keep this true for now to test the line shader
    this.drawAsLines = particleOptions.drawAsLines ?? false;
    this.renderTrails = particleOptions.renderTrails ?? true;

    this.particleSpeed = (particleOptions.speed ?? 0.001) / 1000;
    this.pixelRatio = particleOptions.pixelRatio ?? (dpr > 1 ? 1 : 2);

    const decodeMin = particleOptions.decodeMin ?? 0;
    const decodeMax = particleOptions.decodeMax ?? 1;
    const dAbs = Math.max(Math.abs(decodeMin), Math.abs(decodeMax));
    this.decodeMagnitudeMax = particleOptions.decodeMagnitudeMax ?? dAbs * Math.SQRT2;

    const fieldColorMin = particleOptions.fieldColorMin ?? 0;
    const fieldColorMax = particleOptions.fieldColorMax ?? this.decodeMagnitudeMax;
    if (fieldColorMin < 0) {
      throw new Error("ParticleLayer: fieldColorMin must be >= 0");
    }
    if (fieldColorMax < fieldColorMin) {
      throw new Error("ParticleLayer: fieldColorMax must be >= fieldColorMin");
    }

    /** Encode/decode scalar in RTT with a ceiling >= fieldColorMax so values can reach color thresholds (decodeMagnitudeMax alone can be smaller). */
    const scalarEncodeMax = Math.max(this.decodeMagnitudeMax, fieldColorMax);

    // generate a random particle texture for the previous state
    this.particleTexturePrev = this.generateRandomizeParticleTexture(this.numParticles, this.numParticles);

    // create two ping-pong render targets for particle state
    this.particleTexture0 = new three.WebGLRenderTarget(this.numParticles, this.numParticles, {
      count: 2,
      stencilBuffer: false,
      depthBuffer: false,
    });

    this.particleTexture0.texture = this.particleTexturePrev.clone();
    this.particleTexture1 = this.particleTexture0.clone();

    // create an RTT material that advances particle positions from decoded tile values
    // this is where the particle simulation happens
    this.rttMaterial = new three.RawShaderMaterial({
      glslVersion: three.GLSL3,
      uniforms: {
        tileGridPass: { value: this.tileGridPass },
        uniformSpeed: { value: particleOptions.uniformSpeed ?? false },
        canvasDiagonal: { value: 0 },
        angleDirectionShiftSkip: {
          value: particleOptions.angleDirectionShiftSkip ?? 90,
        },
        rttXY: { value: [0, 0] },
        rttScale: { value: 1 },
        rttTexture: {
          value: this.particleTexture0.texture,
        },
        tileScale: { value: 0 },
        useAlphaAsMask: { value: particleOptions.useAlphaAsMask ?? false },
        tilePosition: { value: [0, 0] },
        time: { value: 0 },
        timestep: { value: 0 },
        tex0xy: { value: [0, 0] },
        tex1xy: { value: [0, 0] },
        tex0size: { value: 1 },
        tex1size: { value: 1 },
        tex0: {
          value: null,
        },
        tex1: {
          value: null,
        },
        rttScalarTexture: {
          value: this.particleTexture0.textures[1],
        },
      },
      vertexShader: RTT_VERTEX,
      fragmentShader: RTT_FRAGMENT,
      defines: {
        D_MIN: (particleOptions.decodeMin ?? 0).toFixed(2),
        D_MAX: (particleOptions.decodeMax ?? 1).toFixed(2),
        D_CHANNELS: particleOptions.decodeChannels ?? "rg",
        D_WAVES: particleOptions.decodeAsWaves ? "true" : "false",
        FIELD_MAGNITUDE_MAX: scalarEncodeMax.toFixed(4),
      },
      depthTest: false,
      depthWrite: false,
    });

    // create an orthographic camera for the RTT scene
    this.rttCamera = new three.OrthographicCamera(
      -this.numParticles / 2,
      this.numParticles / 2,
      this.numParticles / 2,
      -this.numParticles / 2,
      -100,
      100,
    );

    // create a quad mesh for the RTT scene
    this.rttMesh = new three.Mesh(new three.PlaneGeometry(this.numParticles, this.numParticles), this.rttMaterial);
    this.rttScene.add(this.rttMesh);

    // create an accumulator render target used to build frame trails/fade
    this.accumulator = new three.WebGLRenderTarget(16, 16, {
      minFilter: three.LinearFilter,
      magFilter: three.LinearFilter,
      stencilBuffer: false,
      format: three.RGBAFormat,
      depthBuffer: false,
    });

    // create a particle background mesh
    this.particleBackground = new three.Mesh(
      new three.PlaneGeometry(2, 2),
      new three.RawShaderMaterial({
        glslVersion: three.GLSL3,
        transparent: true,
        blending: three.CustomBlending,
        blendEquationAlpha: three.ReverseSubtractEquation,
        vertexShader: "precision highp float;in vec3 position;void main(){gl_Position=vec4(position,1.);}",
        fragmentShader: `precision highp float;out vec4 fragColor;void main(){fragColor=vec4(.0,.0,.0,${this.fadeFactor.toFixed(
          2,
        )});}`,
      }),
    );

    const pointSize = dpr * this.pixelRatio * this.particleSize;
    const lineLength = dpr * this.particleSize;

    // create a particle material that renders the particles as points or line segments
    // this is where the particle rendering happens
    this.particleMaterial = new three.RawShaderMaterial({
      glslVersion: three.GLSL3,
      uniforms: {
        // When true, point size scales slightly with motion `speed` (vertex shader).
        fastIsLarger: { value: particleOptions.fastIsLarger ?? true },

        // Width/height of the particle state texture in texels (`maxAmount`); maps vertex index → UV.
        rttSize: { value: this.numParticles },

        // Latest GPGPU pass: encoded particle positions (RGBA packed coords + parity bit in R).
        rttTexture: {
          value: this.particleTexture1.texture,
        },
        // Snapshot of positions at RTT tick start; used with rttTexture to compute motion `speed`.
        rttTexturePrev: {
          value: this.particleTexturePrev,
        },

        // Second MRT: normalized scalar field magnitude (R); decoded in VS as fieldScalar.
        rttScalarTexture: {
          value: this.particleTexture0.textures[1],
        },

        // Internal texture scale for advection (matches particle accumulator extent).
        // Used in VS to convert normalized position delta to screen units for `speed`.
        screenSize: { value: 1 },

        // Map canvas area in px^2 (`width * height`); with `PARTICLE_DENSITY` caps how many particle
        // indices are drawn (see vertex shader density test).
        particleDensityArea: { value: 1 },
        // Frame interval in seconds; multiplies position delta for motion `speed`. */
        renderStepSize: { value: 0 },
        // Reserved / unused by current particle shaders (legacy hook).
        extrapolationFactor: { value: 0 },
        // Lower bound for fragment color mix (same units as decoded field magnitude, e.g. m/s for wind).
        fieldColorMin: { value: fieldColorMin },
        // Upper bound for full fast color in fragment mix.
        fieldColorMax: { value: fieldColorMax },
      },
      vertexShader: this.drawAsLines ? PARTICLE_LINE_VERTEX : PARTICLE_POINT_VERTEX,
      fragmentShader: PARTICLE_FRAGMENT,
      defines: {
        // Define to tell the shader whether we're drawing line. This define is used in the fragment shader.
        DRAW_AS_LINES: this.drawAsLines ? "1" : "0",
        // Base tint (vec4 string literal) when field magnitude ≤ fieldColorMin.
        PARTICLE_COLOR: this.particleColor,
        // Fast tint (vec4 string literal) when field magnitude ≥ fieldColorMax.
        PARTICLE_FAST_COLOR: this.particleFastColor,
        // Motion reference (px/s) for opacity motionFade in fragment shader.
        PARTICLE_MAX_SPEED: this.particleFastSpeed.toFixed(2),
        // Decodes rttScalarTexture R channel back to field magnitude (must match RTT FIELD_MAGNITUDE_MAX).
        PARTICLE_FIELD_MAGNITUDE_MAX: scalarEncodeMax.toFixed(4),
        // Particles per 1000 px² map area; used with particleDensityArea in VS culling.
        PARTICLE_DENSITY: this.particleDensity.toFixed(2),
        // Point diameter or line length in pixels (draw mode dependent).
        PARTICLE_SIZE: (this.drawAsLines ? lineLength : pointSize).toFixed(1),
      },
      transparent: true,
      depthTest: false,
      depthWrite: false,
      linewidth: this.pixelRatio, // not guaranteed to work, but try anyway
    });

    // create a buffer geometry for the particles

    if (this.drawAsLines) {
      const lineGeometry = new three.BufferGeometry();

      // create a line geometry for the particles, * 2 because we have two vertices per line particle
      const totalVertices = 2 * this.numParticles * this.numParticles;
      // why 3 when we only use the x coordinate? Because three.js requires a valid geometry and so we need to pass a vec3
      const fakePositions = new Float32Array(totalVertices * 3);

      // populate the position attribute for the line geometry
      for (let i = 0; i < totalVertices; i++) {
        fakePositions[i * 3] = i;
      }

      // set the position attribute for the line geometry
      lineGeometry.setAttribute("position", new three.Float32BufferAttribute(fakePositions, 3));

      // we need to fake the bounding sphere because we don't have a valid geometry
      // and we don't want three.js to have a nervous breakdown
      lineGeometry.boundingSphere = new three.Sphere(new three.Vector3(0, 0, 0), Number.POSITIVE_INFINITY);
      lineGeometry.computeBoundingSphere = () => {};

      // create a three.js line segments
      this.particles = new three.LineSegments(lineGeometry, this.particleMaterial);
      this.particleMaterial.needsUpdate = true;
    } else {
      const pointGeometry = new three.BufferGeometry();
      // create a point geometry for the particles
      const totalVertices = this.numParticles * this.numParticles;
      const fakePositions = new Float32Array(totalVertices * 3);

      // populate the position attribute for the point geometry
      for (let i = 0; i < totalVertices; i++) {
        fakePositions[i * 3] = i;
      }

      // set the position attribute for the point geometry
      pointGeometry.setAttribute("position", new three.Float32BufferAttribute(fakePositions, 3));

      // we need to fake the bounding sphere because we don't have a valid geometry
      // and we don't want three.js to have a nervous breakdown
      pointGeometry.boundingSphere = new three.Sphere(new three.Vector3(0, 0, 0), Number.POSITIVE_INFINITY);
      pointGeometry.computeBoundingSphere = () => {};

      this.particles = new three.Points(pointGeometry, this.particleMaterial);
      this.particleMaterial.needsUpdate = true;
    }

    // disable frustum culling for the particles
    this.particles.frustumCulled = false;

    // create a material for the trails.
    const particlesResultMaterial = new three.RawShaderMaterial({
      glslVersion: three.GLSL3,
      premultipliedAlpha: true,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        opacity: { value: 1 },
        tex0: {
          value: this.accumulator.texture,
        },
      },
      vertexShader: ACCUMULATOR_VERTEX,
      fragmentShader: ACCUMULATOR_FRAGMENT,
    });

    this.accumulatorDrawMesh = new three.Mesh(new three.PlaneGeometry(1, 1), particlesResultMaterial);
    this.accumulatorDrawMesh.renderOrder = 2;
    this.slippyTilesGroup.add(this.accumulatorDrawMesh);

    if (USE_DEBUG_PARTICLES_TEXTURE === false) {
      this.globeTilesService.visibleTilesAreaMesh.visible = true;
      this.globeTilesService.visibleTilesAreaMesh.material = particlesResultMaterial;
    }

    setInterval(() => {
      if (this.renderer) {
        this.randomizeParticles(false);
      }
    }, this.refreshInterval);

    this.on("moveend", () => {
      this.refresh();
    });
  }

  /**
   * Generates a seeded random particle texture for the given width and height.
   * This is used to initialize new particles in "random" positions.
   */
  private generateRandomizeParticleTexture(w: number, h: number) {
    const data = new Uint8Array(4 * w * h);
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.floor(256 * this.randomNumberGen.next());
    }

    return new three.DataTexture(data, w, h);
  }

  /**
   * Generates a random particle texture copies it to the previous, current, and next particle textures.
   * Essentially creating new particles in "random" positions.
   */
  private randomizeParticles(all: boolean) {
    if (!this.renderer) {
      return;
    }
    let w = this.numParticles;
    let h = this.numParticles;
    const xy = new three.Vector2(0, 0);

    if (!all) {
      const PART = this.numParticles / 4;
      w = PART;
      h = PART;

      xy.x = Math.floor((this.randomNumberGen.next() * this.numParticles) / PART) * PART;
      xy.y = Math.floor((this.randomNumberGen.next() * this.numParticles) / PART) * PART;
    }

    const newData = this.generateRandomizeParticleTexture(w, h);
    const zeroScalar = new three.DataTexture(new Uint8Array(4 * w * h), w, h);
    zeroScalar.needsUpdate = true;
    this.renderer.copyTextureToTexture(newData, this.particleTexturePrev, null, xy);
    this.renderer.copyTextureToTexture(newData, this.particleTexture0.texture, null, xy);
    this.renderer.copyTextureToTexture(newData, this.particleTexture1.texture, null, xy);
    this.renderer.copyTextureToTexture(zeroScalar, this.particleTexture0.textures[1], null, xy);
    this.renderer.copyTextureToTexture(zeroScalar, this.particleTexture1.textures[1], null, xy);
    newData.dispose();
    zeroScalar.dispose();

    this.forceRender = all;
  }

  /**
   * Returns the ratio between the number of actually visible particle
   * (to statisfy the specified density) and the maximum amount (maxAmount).
   *
   * Value >1 mean that more particles would be utilized if availabe.
   *
   * Useful for debugging and fine-tuning client application.
   */
  public getParticleUtilization(): number {
    const area = this.particleMaterial.uniforms.particleDensityArea.value;
    return ((this.particleDensity / 1000) * area) / (this.numParticles * this.numParticles);
  }

  /**
   * Sets the opacity of the particle layer's objects.
   */
  public setOpacity(opacity: number) {
    super.setOpacity(opacity);
    (<three.RawShaderMaterial>this.accumulatorDrawMesh.material).uniforms.opacity.value = opacity;
  }

  /**
   /**
    * Performs the disposal of the particle layer's objects using three.js resource management methods.
    */
  protected override disposeObjects() {
    super.disposeObjects();

    // this.particleTexturePrev.texture.dispose();
    this.particleTexturePrev.dispose();
    this.particleTexture0.textures[1].dispose();
    this.particleTexture0.texture.dispose();
    this.particleTexture0.dispose();
    this.particleTexture1.textures[1].dispose();
    this.particleTexture1.texture.dispose();
    this.particleTexture1.dispose();
    this.accumulator.texture.dispose();
    this.accumulator.dispose();

    this.rttMesh.geometry.dispose();
    (<three.Material>this.rttMesh.material).dispose();
    this.particleBackground.geometry.dispose();
    (<three.Material>this.particleBackground.material).dispose();
    this.particles.geometry.dispose();
    (<three.Material>this.particles.material).dispose();
    this.accumulatorDrawMesh.geometry.dispose();
    (<three.Material>this.accumulatorDrawMesh.material).dispose();
  }

  /**
   * Updates the rendering size of the particle layer.
   * Determines particle size based on the map zoom, pitch, and resolution scale factor.
   * Called in refresh method
   */
  private updateRenderingSize() {
    const mapInstance = this.getMapOrThrow();
    const zoom = mapInstance.getZoom();
    const pitch = mapInstance.getPitch();
    const canvas = mapInstance.getCanvas();
    const width = canvas.width;
    const height = canvas.height;

    const { resolutionScaleFactor } = getGridSizeForPitchAndMapDimensions(pitch, mapInstance);
    const baseTextureSize = 1024 * 2 * this.pixelRatio * resolutionScaleFactor;

    let size = baseTextureSize;

    if (this.getMapOrThrow().isGlobeProjection() === true) {
      if (zoom > GLOBE_MAX_ZOOM + 1) {
        size = baseTextureSize;
      } else {
        size = Math.max(baseTextureSize * (zoom * 0.75), 1024);
      }
    } else {
      size = Math.round(this.pixelRatio * this.extentScale * Math.max(width, height));
    }

    size = Math.min(size, this.getRendererOrThrow().capabilities.maxTextureSize);

    const canvasDiagonal = Math.sqrt(width * width + height * height) * (devicePixelRatio === 1 ? 2 : 1);
    this.rttMaterial.uniforms.canvasDiagonal.value = canvasDiagonal;

    this.particleMaterial.uniforms.screenSize.value = size;
    this.particleMaterial.uniforms.particleDensityArea.value = width * height;
    this.accumulator.setSize(size, size);

    this.slippyTilesGroup.remove(this.accumulatorDrawMesh);
    this.slippyTilesGroup.add(this.accumulatorDrawMesh);
  }

  /**
   * Called by onMoveEnd in parent class.
   * If `useDebounce` is true, the refresh will be debounced by 100ms.
   */
  protected override refresh() {
    super.refresh();

    if (this.getMapOrThrow().isGlobeProjection() === false) {
      this.refreshMercator();
    } else {
      this.refreshGlobe();
    }

    this.updateRenderingSize();

    const renderer = this.getRendererOrThrow();
    renderer.setRenderTarget(this.accumulator);
    renderer.setClearAlpha(0);
    renderer.clearColor();
    renderer.setRenderTarget(null);
    this.randomizeParticles(true);
  }

  /**
   * Called by prerender in parent class.
   * This is the main function for rendering and updating the particle layer.
   */
  protected override prerenderInternal() {
    const now = performance.now();
    const timeSinceLastRender = now - this.lastRenderTime;

    const doRtt = this.forceRender || timeSinceLastRender > this.rttTimestep || !this.prevRenderTime;
    if (doRtt) {
      this.rttMaterial.uniforms.timestep.value = this.particleSpeed * timeSinceLastRender;
      // Snapshot current read texture so VS can compute diff against true previous positions.
      const readParticlesBeforeRtt = this.flipFlop ? this.particleTexture1 : this.particleTexture0;
      this.getRendererOrThrow().setRenderTarget(readParticlesBeforeRtt);
      this.getRendererOrThrow().copyFramebufferToTexture(this.particleTexturePrev, new three.Vector2(0, 0));

      this.prevRenderTime = this.lastRenderTime;
      this.lastRenderTime = now;

      if (this.getMapOrThrow().isGlobeProjection() === false) {
        this.prerenderInternalMercator();
      } else {
        this.prerenderInternalGlobe();
      }

      const currentParticles = !this.flipFlop ? this.particleTexture0 : this.particleTexture1;
      this.particleMaterial.uniforms.rttTexture.value = currentParticles.texture;
      this.particleMaterial.uniforms.rttScalarTexture.value = currentParticles.textures[1];

      this.tileGridPass = this.tileGridPass === 0 ? 1 : 0;
    }
    const renderStepSize = this.lastRenderTime - this.prevRenderTime;

    this.particleMaterial.uniforms.renderStepSize.value = renderStepSize / 1000;

    const renderer = this.getRendererOrThrow();
    renderer.setRenderTarget(this.accumulator);
    if (!this.renderTrails) {
      renderer.setClearAlpha(0);
      renderer.clearColor();
    } else {
      renderer.render(this.particleBackground, this.camera);
    }
    renderer.render(this.particles, this.camera);
    renderer.setRenderTarget(null);

    if (this.particlesTexturePreview !== undefined) {
      const width = this.accumulator.width;
      const height = this.accumulator.height;
      const buffer = new Uint8Array(width * height * 4);

      renderer.readRenderTargetPixels(this.accumulator, 0, 0, width, height, buffer);

      this.particlesTexturePreview.update(buffer, width, height);
    }
  }

  /**
   * Updates the RTT material uniforms for the mercator projection.
   */
  private refreshMercator() {
    const extent = this.getVisibleExtent(this.extentScale);

    if (!extent) throw new Error("The extent is null");

    const scale = Math.max(extent[2] - extent[0], extent[3] - extent[1]) / EARTH_PERIMETER_M;

    const XY = [
      0.5 + (extent[2] + extent[0]) / 2 / EARTH_PERIMETER_M - scale / 2,
      0.5 - (extent[3] + extent[1]) / 2 / EARTH_PERIMETER_M - scale / 2,
    ];

    this.rttMaterial.uniforms.rttScale.value = scale;
    this.rttMaterial.uniforms.rttXY.value = XY;
  }

  /**
   * Renders the particles for the mercator projection.
   */
  private prerenderInternalMercator() {
    const gridWidth = this.slippyTiles.length;
    for (let _x = 0; _x < gridWidth; _x++) {
      const gridHeight = this.slippyTiles[0].length;
      for (let _y = 0; _y < gridHeight; _y++) {
        const tile = this.slippyTiles[_x][_y];

        if (!tile.visible) {
          continue;
        }

        const tileMaterial = <three.RawShaderMaterial>tile.material;
        const uniforms = tileMaterial.uniforms;

        if (!uniforms) continue;

        const scale = tile.scale.x;
        this.rttMaterial.uniforms.tileScale.value = scale;
        this.rttMaterial.uniforms.tilePosition.value = [tile.position.x / scale - 0.5, -tile.position.y / scale - 0.5];

        this.rttMaterial.uniforms.tileGridPass.value = this.tileGridPass;
        this.rttMaterial.uniforms.time.value = uniforms.time.value;
        this.rttMaterial.uniforms.tex0.value = uniforms.tex0.value;
        this.rttMaterial.uniforms.tex0xy.value = uniforms.tex0xy.value;
        this.rttMaterial.uniforms.tex0size.value = uniforms.tex0size.value;
        this.rttMaterial.uniforms.tex1.value = uniforms.tex1.value;
        this.rttMaterial.uniforms.tex1xy.value = uniforms.tex1xy.value;
        this.rttMaterial.uniforms.tex1size.value = uniforms.tex1size.value;

        const readParticles = this.flipFlop ? this.particleTexture1 : this.particleTexture0;
        this.rttMaterial.uniforms.rttTexture.value = readParticles.texture;
        this.rttMaterial.uniforms.rttScalarTexture.value = readParticles.textures[1];

        const renderer = this.getRendererOrThrow();
        renderer.setRenderTarget(this.flipFlop ? this.particleTexture0 : this.particleTexture1);
        renderer.render(this.rttScene, this.rttCamera);

        this.flipFlop = !this.flipFlop;
      }
    }
  }

  /**
   * Called by `refresh` method.
   * Refresh logic is different for globe and mercator projection.
   */
  private refreshGlobe() {
    // For the globe projection this is different then for the mercator projection
    // With each refresh the spher mesh fragment is updated to fit the extent of the visible tiles
    // Look also at prerenderInternalMercator vs prerenderInternalGlobe
    this.rttMaterial.uniforms.rttScale.value = 1;
    this.rttMaterial.uniforms.rttXY.value = [0, 0];
  }

  /**
   * Called by `prerenderInternal` method.
   * Prerender logic is different for globe and mercator projection.
   */
  private prerenderInternalGlobe() {
    const tilesIds = [...this.globeTilesService.currentTilesMeshes.entries()].map(([tileId]) => tileId);

    const center = this.getMapOrThrow().getCenter();
    const zoom = this.getMapOrThrow().getZoom();

    const amountOfTiles = tilesIds.length;
    const gridDimension = Math.floor(Math.sqrt(amountOfTiles));
    /**
     * For zoom level <= GLOBE_MAX_ZOOM the "seam" of the globe particles texture is visible,
     * so we have to care about that and make the stiching be on the back side.
     *
     * To do that we calculate the `globeSideFactor`.
     * lng -90;90 -> 0 (the "seam" is in the back as default)
     * lng 90;180 -> 1
     * lng -90;-180 -> -1
     */
    const globeSideFactor = center.lng >= 0 ? Math.floor(center.lng / 90) : -1 * Math.floor(Math.abs(center.lng) / 90);

    let textureTileOffset = 0;

    // Reset the rotation
    this.globeTilesService.visibleTilesAreaMesh.rotation.y = 0;
    if (zoom <= GLOBE_MAX_ZOOM) {
      /**
       * Now we have to rotate the globe to make the "seam" be on the back side.
       */
      const backSideRotationY = globeSideFactor * three.MathUtils.degToRad(180);
      this.globeTilesService.visibleTilesAreaMesh.rotation.y = backSideRotationY;
      /**
       * Since we rotate the globe 180 degrees, we have to move the texture tiles.
       * We have to move them by the half of the gridDimension.
       */
      textureTileOffset = globeSideFactor * (gridDimension / 2);
    }

    const { frameA, frameB, mix } = this.getCurrentFrames();

    if (!frameA || !frameB) {
      return;
    }

    for (const tileId of tilesIds) {
      const index = tilesIds.indexOf(tileId);
      const x = (Math.floor(index / gridDimension) + textureTileOffset + gridDimension) % gridDimension;
      const y = index % gridDimension;
      this.rttMaterial.uniforms.tileScale.value = 1 / gridDimension;
      this.rttMaterial.uniforms.tilePosition.value = [x, y];

      const tilesPair = this.getTilesPair(frameA, frameB, tileId);

      if (tilesPair === null) {
        continue;
      }

      const { tileA, tileB } = tilesPair;

      this.rttMaterial.uniforms.tileGridPass.value = this.tileGridPass;
      this.rttMaterial.uniforms.time.value = mix;
      this.rttMaterial.uniforms.tex0.value = tileA.tile.texture;
      this.rttMaterial.uniforms.tex0xy.value = tileA.xy;
      this.rttMaterial.uniforms.tex0size.value = tileA.size;
      this.rttMaterial.uniforms.tex1.value = tileB.tile.texture;
      this.rttMaterial.uniforms.tex1xy.value = tileB.xy;
      this.rttMaterial.uniforms.tex1size.value = tileB.size;
      const readParticlesGlobe = this.flipFlop ? this.particleTexture1 : this.particleTexture0;
      this.rttMaterial.uniforms.rttTexture.value = readParticlesGlobe.texture;
      this.rttMaterial.uniforms.rttScalarTexture.value = readParticlesGlobe.textures[1];

      const renderer = this.getRendererOrThrow();
      renderer.setRenderTarget(this.flipFlop ? this.particleTexture0 : this.particleTexture1);
      renderer.render(this.rttScene, this.rttCamera);

      this.flipFlop = !this.flipFlop;
    }
  }

  /**
   * Called by render in parent class.
   */
  protected override renderInternal() {
    if (this.getMapOrThrow().isGlobeProjection() === false) {
      const scale = this.rttMaterial.uniforms.rttScale.value;

      this.accumulatorDrawMesh.position.x = 0.5 * scale + this.rttMaterial.uniforms.rttXY.value[0];
      this.accumulatorDrawMesh.position.y = -0.5 * scale - this.rttMaterial.uniforms.rttXY.value[1];
      this.accumulatorDrawMesh.scale.x = this.accumulatorDrawMesh.scale.y = scale;
    }
  }
}

export { ParticleLayer };
